<template>
    <Head title="PM Schedules" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Header -->
                <div class="flex justify-between items-center mb-6">
                    <div class="flex items-center gap-3">
                        <div>
                            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">PM Schedules</h1>
                            <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Proactive preventive maintenance schedule management</p>
                        </div>
                        <InfoButton title="PM Schedules Overview" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The PM Schedules page displays all preventive maintenance schedules across your assets. PM
                                schedules are created when PM Plans are linked to assets and automatically generate work orders
                                based on calendar dates or meter readings. Use this page to monitor upcoming maintenance,
                                identify overdue schedules, and bulk-generate work orders to keep your assets maintained
                                proactively.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Quick Stats:</strong> View Due Today, Next 7 Days, Next 30 Days, and Overdue PM
                                  schedules at a glance<br>
                                • <strong>Time Period Tabs:</strong> Switch between Overdue, Today, Next 7 Days, Next 30 Days,
                                  and Next 3 Months to focus on relevant time frames<br>
                                • <strong>Search & Filter:</strong> Find schedules by asset name, tag, or PM plan name. Filter
                                  by location, PM plan, or asset category<br>
                                • <strong>Bulk Actions:</strong> Select multiple schedules and click "Bulk Create WOs" to
                                  generate work orders for multiple assets at once<br>
                                • <strong>Generate WO:</strong> Click "Generate WO" for individual schedules to create a work
                                  order immediately (without waiting for automatic generation)<br>
                                • <strong>View Asset:</strong> Click to navigate to asset details and manage PM schedule
                                  configuration<br>
                                • <strong>Status Column:</strong> Shows "Available" (ready for WO generation) or "WO Active"
                                  (already has an active work order)<br>
                                • <strong>Overdue Indicators:</strong> Schedules past due date appear highlighted with red
                                  background and "Overdue" badge
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Address overdue schedules first</strong> - These represent delayed maintenance that
                                  could lead to breakdowns<br>
                                • <strong>Use bulk creation</strong> for weekly PM planning - Select all schedules for the
                                  upcoming week and generate work orders in one action<br>
                                • <strong>"WO Active" status</strong> prevents duplicate work orders - Complete or close the
                                  existing work order before generating a new one<br>
                                • <strong>Next PM Date</strong> column shows when work orders will auto-generate (if
                                  configured)<br>
                                • <strong>Work Days</strong> indicates estimated time to complete the PM work<br>
                                • <strong>View PM Plans</strong> button navigates to PM Plan templates for editing frequencies
                                  and tasks<br>
                                • PM schedules cannot be created on this page - they are automatically created when PM Plans
                                  are linked to assets (Settings → Maintenance → Assets → PM Schedule tab)
                            </p>
                        </InfoButton>
                    </div>
                    <Link
                        :href="route('settings.maintenance.pm-plans.index')"
                        class="inline-flex items-center px-4 py-2 bg-white dark:bg-secondary-700 border border-gray-300 dark:border-secondary-600 rounded-md font-semibold text-xs text-gray-700 dark:text-secondary-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        View PM Plans
                    </Link>
                </div>

                <!-- Statistics Cards -->
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <!-- Due Today -->
                    <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6 border border-gray-200 dark:border-secondary-700">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                                <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Due Today</p>
                                <p class="text-2xl font-semibold text-blue-600 dark:text-blue-400">{{ stats.due_today }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Next 7 Days -->
                    <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6 border border-gray-200 dark:border-secondary-700">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                                <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Next 7 Days</p>
                                <p class="text-2xl font-semibold text-green-600 dark:text-green-400">{{ stats.next_7_days }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Next 30 Days -->
                    <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6 border border-gray-200 dark:border-secondary-700">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 bg-yellow-100 dark:bg-yellow-900/30 rounded-md p-3">
                                <svg class="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Next 30 Days</p>
                                <p class="text-2xl font-semibold text-yellow-600 dark:text-yellow-400">{{ stats.next_30_days }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Overdue -->
                    <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6 border border-gray-200 dark:border-secondary-700">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 bg-red-100 dark:bg-red-900/30 rounded-md p-3">
                                <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Overdue</p>
                                <p class="text-2xl font-semibold text-red-600 dark:text-red-400">{{ stats.overdue }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tabs -->
                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow mb-6 border border-gray-200 dark:border-secondary-700">
                    <div class="border-b border-gray-200 dark:border-secondary-700">
                        <nav class="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                            <button
                                v-for="tabOption in tabs"
                                :key="tabOption.value"
                                @click="changeTab(tabOption.value)"
                                :class="[
                                    'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm',
                                    tab === tabOption.value
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-300 hover:border-gray-300 dark:hover:border-secondary-600'
                                ]"
                            >
                                {{ tabOption.label }}
                                <span
                                    v-if="tabOption.count !== undefined"
                                    :class="[
                                        'ml-2 py-0.5 px-2 rounded-full text-xs font-medium',
                                        tab === tabOption.value
                                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                            : 'bg-gray-100 dark:bg-secondary-700 text-gray-600 dark:text-secondary-300'
                                    ]"
                                >
                                    {{ tabOption.count }}
                                </span>
                            </button>
                        </nav>
                    </div>

                    <!-- Filters and Bulk Actions -->
                    <div class="p-4 border-b border-gray-200 dark:border-secondary-700">
                        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <!-- Left side - Search and Filters -->
                            <div class="flex flex-col sm:flex-row gap-4 flex-1">
                                <!-- Search -->
                                <div class="flex-1 max-w-md">
                                    <div class="relative">
                                        <input
                                            v-model="searchQuery"
                                            type="text"
                                            placeholder="Search asset or PM plan..."
                                            class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            @input="debounceSearch"
                                        />
                                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <!-- Filter Dropdowns -->
                                <div class="flex gap-2">
                                    <select
                                        v-model="selectedFilters.location_id"
                                        class="rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                                        @change="applyFilters"
                                    >
                                        <option value="">All Locations</option>
                                        <option v-for="location in locations" :key="location.id" :value="location.id">
                                            {{ location.path_with_type }}
                                        </option>
                                    </select>

                                    <select
                                        v-model="selectedFilters.pm_plan_id"
                                        class="rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                                        @change="applyFilters"
                                    >
                                        <option value="">All PM Plans</option>
                                        <option v-for="plan in pmPlans" :key="plan.id" :value="plan.id">
                                            {{ plan.name }}
                                        </option>
                                    </select>

                                    <select
                                        v-model="selectedFilters.category_id"
                                        class="rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                                        @change="applyFilters"
                                    >
                                        <option value="">All Categories</option>
                                        <option v-for="category in categories" :key="category.id" :value="category.id">
                                            {{ category.name }}
                                        </option>
                                    </select>

                                    <button
                                        v-if="hasActiveFilters"
                                        @click="clearFilters"
                                        class="px-3 py-1.5 text-sm text-gray-600 dark:text-secondary-300 hover:text-gray-800 dark:hover:text-white border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-600"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>

                            <!-- Right side - Bulk Actions -->
                            <div v-if="selectedSchedules.length > 0" class="flex items-center gap-2">
                                <span class="text-sm text-gray-600 dark:text-secondary-300">
                                    {{ selectedSchedules.length }} selected
                                </span>
                                <button
                                    v-if="canCreatePmOrder"
                                    @click="bulkCreateWorkOrders"
                                    :disabled="bulkCreating"
                                    class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition disabled:opacity-50"
                                >
                                    <svg v-if="!bulkCreating" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    <svg v-else class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {{ bulkCreating ? 'Creating...' : 'Bulk Create WOs' }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Table -->
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                            <thead class="bg-gray-50 dark:bg-secondary-750">
                                <tr class="bg-gray-50 dark:bg-secondary-750">
                                    <th class="px-6 py-3 text-left bg-gray-50 dark:bg-secondary-750">
                                        <input
                                            type="checkbox"
                                            :checked="allSelected"
                                            @change="toggleSelectAll"
                                            class="rounded border-gray-300 dark:border-secondary-600 text-blue-600 focus:ring-blue-500"
                                        />
                                    </th>
                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider bg-gray-50 dark:bg-secondary-750">
                                        Asset
                                    </th>
                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider bg-gray-50 dark:bg-secondary-750">
                                        PM Plan
                                    </th>
                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider bg-gray-50 dark:bg-secondary-750">
                                        Location
                                    </th>
                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider bg-gray-50 dark:bg-secondary-750">
                                        Next PM Date
                                    </th>
                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider bg-gray-50 dark:bg-secondary-750">
                                        Work Days
                                    </th>
                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider bg-gray-50 dark:bg-secondary-750">
                                        Status
                                    </th>
                                    <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider bg-gray-50 dark:bg-secondary-750">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                                <tr
                                    v-for="schedule in schedules.data"
                                    :key="schedule.id"
                                    :class="[
                                        'hover:bg-gray-50 dark:hover:bg-secondary-750',
                                        schedule.is_overdue && 'bg-red-50 dark:bg-red-900/20'
                                    ]"
                                >
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <input
                                            type="checkbox"
                                            :value="schedule.id"
                                            v-model="selectedSchedules"
                                            :disabled="schedule.has_active_wo"
                                            class="rounded border-gray-300 dark:border-secondary-600 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="flex items-center">
                                            <div>
                                                <div class="text-sm font-medium text-gray-900 dark:text-white">
                                                    {{ schedule.asset.name }}
                                                </div>
                                                <div class="text-sm text-gray-500 dark:text-secondary-400">
                                                    {{ schedule.asset.asset_tag }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="text-sm text-gray-900 dark:text-white">{{ schedule.pm_plan.name }}</div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="text-sm text-gray-900 dark:text-white">
                                            {{ schedule.asset.location?.name || '-' }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="flex items-center gap-2">
                                            <span
                                                :class="[
                                                    'text-sm',
                                                    schedule.is_overdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-900 dark:text-white'
                                                ]"
                                            >
                                                {{ formatDate(schedule.next_pm_date) }}
                                            </span>
                                            <span
                                                v-if="schedule.is_overdue"
                                                class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400"
                                            >
                                                Overdue
                                            </span>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {{ schedule.work_days }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <span
                                            v-if="schedule.has_active_wo"
                                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400"
                                        >
                                            WO Active
                                        </span>
                                        <span
                                            v-else
                                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400"
                                        >
                                            Available
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div class="flex items-center justify-end gap-2">
                                            <Link
                                                :href="route('settings.maintenance.assets.show', schedule.asset.id)"
                                                class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                            >
                                                View Asset
                                            </Link>
                                            <span class="text-gray-300 dark:text-secondary-600">|</span>
                                            <Link
                                                v-if="canCreatePmOrder && !schedule.has_active_wo"
                                                :href="route('work-orders.createFromPmSchedule', schedule.id)"
                                                class="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                                            >
                                                Generate WO
                                            </Link>
                                            <span v-else-if="schedule.has_active_wo">
                                                <Link
                                                    :href="route('work-orders.show', schedule.active_wo.id)"
                                                    class="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300"
                                                >
                                                    View WO #{{ schedule.active_wo.work_order_number }}
                                                </Link>
                                            </span>
                                            <span v-else class="text-gray-400 dark:text-secondary-500">
                                                No Permission
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Empty State -->
                    <div v-if="schedules.data.length === 0" class="p-12 text-center">
                        <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No PM schedules found</h3>
                        <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">
                            {{ hasActiveFilters ? 'Try adjusting your filters' : 'No schedules match the selected time period' }}
                        </p>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="schedules.data.length > 0" class="bg-white dark:bg-secondary-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-secondary-700 sm:px-6 rounded-lg shadow">
                    <div class="flex-1 flex justify-between sm:hidden">
                        <Link
                            v-if="schedules.prev_page_url"
                            :href="schedules.prev_page_url"
                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-secondary-600 text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                        >
                            Previous
                        </Link>
                        <Link
                            v-if="schedules.next_page_url"
                            :href="schedules.next_page_url"
                            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-secondary-600 text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                        >
                            Next
                        </Link>
                    </div>
                    <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p class="text-sm text-gray-700 dark:text-secondary-300">
                                Showing
                                <span class="font-medium">{{ schedules.from }}</span>
                                to
                                <span class="font-medium">{{ schedules.to }}</span>
                                of
                                <span class="font-medium">{{ schedules.total }}</span>
                                results
                            </p>
                        </div>
                        <div>
                            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <template v-for="link in schedules.links" :key="link.label">
                                    <Link
                                        v-if="link.url"
                                        :href="link.url"
                                        :class="[
                                            'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                            link.active
                                                ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-600 text-blue-600 dark:text-blue-400'
                                                : 'bg-white dark:bg-secondary-700 border-gray-300 dark:border-secondary-600 text-gray-500 dark:text-secondary-300 hover:bg-gray-50 dark:hover:bg-secondary-600'
                                        ]"
                                        v-html="link.label"
                                    />
                                    <span
                                        v-else
                                        :class="[
                                            'relative inline-flex items-center px-4 py-2 border text-sm font-medium cursor-not-allowed opacity-50',
                                            'bg-white dark:bg-secondary-700 border-gray-300 dark:border-secondary-600 text-gray-400 dark:text-secondary-500'
                                        ]"
                                        v-html="link.label"
                                    />
                                </template>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import { useSecureAxios } from '@/Composables/useSecureAxios';
import route from '@/Utils/route';
import axios from 'axios';
import InfoButton from '@/Components/Common/InfoButton.vue';

// Get secure axios methods
const { post } = useSecureAxios();

const props = defineProps({
    schedules: Object,
    stats: Object,
    tab: String,
    locations: Array,
    pmPlans: Array,
    categories: Array,
    filters: Object,
});

const page = usePage();

// Permissions
const canCreatePmOrder = computed(() => {
    return page.props?.auth?.permissions?.includes('maintenance.work_orders.create_pm_order') || false;
});

// Tabs configuration
const tabs = computed(() => [
    { value: 'overdue', label: 'Overdue', count: props.stats.overdue },
    { value: 'today', label: 'Today', count: props.stats.due_today },
    { value: 'next_7_days', label: 'Next 7 Days', count: props.stats.next_7_days },
    { value: 'next_30_days', label: 'Next 30 Days', count: props.stats.next_30_days },
    { value: 'next_3_months', label: 'Next 3 Months', count: props.stats.next_3_months },
]);

// Filter state
const searchQuery = ref(props.filters.search || '');
const selectedFilters = ref({
    location_id: props.filters.location_id || '',
    pm_plan_id: props.filters.pm_plan_id || '',
    category_id: props.filters.category_id || '',
    without_wo: props.filters.without_wo || false,
});

// Selection state
const selectedSchedules = ref([]);
const bulkCreating = ref(false);

let searchTimeout = null;

const hasActiveFilters = computed(() => {
    return selectedFilters.value.location_id ||
           selectedFilters.value.pm_plan_id ||
           selectedFilters.value.category_id ||
           searchQuery.value;
});

const allSelected = computed(() => {
    const selectableSchedules = props.schedules.data.filter(s => !s.has_active_wo);
    return selectableSchedules.length > 0 && selectedSchedules.value.length === selectableSchedules.length;
});

const toggleSelectAll = () => {
    const selectableSchedules = props.schedules.data.filter(s => !s.has_active_wo);
    if (allSelected.value) {
        selectedSchedules.value = [];
    } else {
        selectedSchedules.value = selectableSchedules.map(s => s.id);
    }
};

const changeTab = (newTab) => {
    selectedSchedules.value = [];
    router.get(route('pm-schedules.index'), {
        tab: newTab,
        search: searchQuery.value,
        ...selectedFilters.value,
    }, {
        preserveState: false,
    });
};

const debounceSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500);
};

const applyFilters = () => {
    selectedSchedules.value = [];
    router.get(route('pm-schedules.index'), {
        tab: props.tab,
        search: searchQuery.value,
        ...selectedFilters.value,
    }, {
        preserveState: true,
        preserveScroll: true,
    });
};

const clearFilters = () => {
    searchQuery.value = '';
    selectedFilters.value = {
        location_id: '',
        pm_plan_id: '',
        category_id: '',
        without_wo: false,
    };
    applyFilters();
};

const bulkCreateWorkOrders = async () => {
    if (selectedSchedules.value.length === 0) {
        alert('Please select at least one PM schedule');
        return;
    }

    if (!confirm(`Create ${selectedSchedules.value.length} work order(s) from selected PM schedules?`)) {
        return;
    }

    bulkCreating.value = true;

    try {
        const response = await post(route('work-orders.bulkCreateFromPmSchedules'), {
            schedule_ids: selectedSchedules.value,
        });

        if (response.data.success) {
            const summary = response.data.summary;
            let message = `Successfully created ${summary.created} work order(s).`;

            if (summary.skipped > 0) {
                message += `\n${summary.skipped} schedule(s) were skipped (already have active work orders).`;
            }

            if (summary.errors > 0) {
                message += `\n${summary.errors} schedule(s) encountered errors.`;
            }

            alert(message);

            // Clear selection and refresh page
            selectedSchedules.value = [];
            router.reload({ preserveScroll: true });
        }
    } catch (error) {
        console.error('Bulk create error:', error);
        alert(error.response?.data?.message || 'An error occurred while creating work orders');
    } finally {
        bulkCreating.value = false;
    }
};

const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};
</script>
